import { supabase } from "./supabase";
import { rpcErrorResult } from "./adminApi";

// ─────────────────────────────────────────────────────────────────────────────
// Squads admin API (round 10, R30/R31). Identity lives on `players`; dated
// team affiliation lives on `squad_memberships`. Every write goes through a
// SECURITY DEFINER RPC — the client never writes squad_memberships directly.
// RPC error mapping reuses adminApi.js's `rpcErrorResult` (42501 → forbidden;
// a message starting with a known stable token → that token; else
// server_error).
// ─────────────────────────────────────────────────────────────────────────────

const SQUAD_BUILD_CODES = new Set([
  "unauthenticated",
  "forbidden",
  "invalid_body",
  "feed_unavailable",
  "method_not_allowed",
  "server_error",
]);

async function functionErrorResult(error, knownCodes) {
  if (error?.context && typeof error.context.json === "function") {
    try {
      const body = await error.context.json();
      const code = knownCodes.has(body?.error) ? body.error : "server_error";
      return { ok: false, code };
    } catch {
      return { ok: false, code: "server_error" };
    }
  }
  return { ok: false, code: "network_error" };
}

/**
 * Fetch every membership of a team for a season (including ended rows)
 * through the admin_squad RPC. Returns an array, or null on error.
 */
export async function adminSquad(teamId, season) {
  try {
    const { data, error } = await supabase.rpc("admin_squad", {
      p_team_id: teamId,
      p_season: season,
    });
    if (error) {
      console.warn("[squadsApi] adminSquad failed:", error.message);
      return null;
    }
    return (Array.isArray(data) ? data : []).map((row) => ({
      membershipId: row.membership_id,
      playerId: row.player_id,
      name: row.name,
      dateOfBirth: row.date_of_birth ?? null,
      externalRef: row.external_ref ?? null,
      startsOn: row.starts_on,
      endsOn: row.ends_on ?? null,
      kind: row.kind,
      endReason: row.end_reason ?? null,
      source: row.source,
    }));
  } catch (err) {
    console.warn("[squadsApi] adminSquad failed:", err?.message || err);
    return null;
  }
}

/**
 * Move a player's current membership to another team. `effective` null (or
 * equal to that membership's start date) is a correction (the row's team_id
 * is updated in place); any later date within the season is a transfer (the
 * old membership is closed off and a new one opened). Returns
 * { ok:true, mode:'correction'|'transfer', membershipId, endedMembershipId? }
 * or { ok:false, code } (unknown_team, invalid_date, no_membership,
 * same_team, forbidden, server_error).
 */
export async function adminMovePlayer(playerId, toTeamId, season, effective = null) {
  try {
    const { data, error } = await supabase.rpc("admin_move_player", {
      p_player_id: playerId,
      p_to_team_id: toTeamId,
      p_season: season,
      p_effective: effective,
    });
    if (error) {
      console.warn("[squadsApi] adminMovePlayer failed:", error.message);
      return rpcErrorResult(error);
    }
    return { ok: true, mode: data?.mode, membershipId: data?.membership_id, endedMembershipId: data?.ended_membership_id };
  } catch (err) {
    console.warn("[squadsApi] adminMovePlayer failed:", err?.message || err);
    return { ok: false, code: "server_error" };
  }
}

/**
 * End a membership. `endsOn` null deletes the row (a correction — the player
 * was never really in that squad); otherwise stamps ends_on/end_reason.
 * Returns { ok:true, mode:'deleted'|'ended' } or { ok:false, code }
 * (unknown_membership, invalid_date, invalid_reason, forbidden, server_error).
 */
export async function adminEndMembership(membershipId, endsOn = null, reason = "released") {
  try {
    const { data, error } = await supabase.rpc("admin_end_membership", {
      p_membership_id: membershipId,
      p_ends_on: endsOn,
      p_reason: reason,
    });
    if (error) {
      console.warn("[squadsApi] adminEndMembership failed:", error.message);
      return rpcErrorResult(error);
    }
    return { ok: true, mode: data?.mode };
  } catch (err) {
    console.warn("[squadsApi] adminEndMembership failed:", err?.message || err);
    return { ok: false, code: "server_error" };
  }
}

/**
 * Add a player the app already knows about (found via adminPlayerByRef) to a
 * squad. Returns { ok:true, membershipId } or { ok:false, code }
 * (unknown_player, unknown_team, invalid_kind, invalid_date,
 * membership_overlap, forbidden, server_error).
 */
export async function adminAddToSquad(playerId, teamId, season, startsOn = null, kind = "squad") {
  try {
    const { data, error } = await supabase.rpc("admin_add_to_squad", {
      p_player_id: playerId,
      p_team_id: teamId,
      p_season: season,
      p_starts_on: startsOn,
      p_kind: kind,
    });
    if (error) {
      console.warn("[squadsApi] adminAddToSquad failed:", error.message);
      return rpcErrorResult(error);
    }
    return { ok: true, membershipId: data };
  } catch (err) {
    console.warn("[squadsApi] adminAddToSquad failed:", err?.message || err);
    return { ok: false, code: "server_error" };
  }
}

/**
 * Look a player up by their RLP external_ref through admin_player_by_ref.
 * Returns { ok:true, found:false } or { ok:true, found:true, player,
 * memberships } on a clean RPC response, or { ok:false, code } on failure.
 */
export async function adminPlayerByRef(externalRef) {
  try {
    const { data, error } = await supabase.rpc("admin_player_by_ref", {
      p_external_ref: externalRef,
    });
    if (error) {
      console.warn("[squadsApi] adminPlayerByRef failed:", error.message);
      return rpcErrorResult(error);
    }
    return {
      ok: true,
      found: Boolean(data?.found),
      player: data?.player ?? null,
      memberships: Array.isArray(data?.memberships) ? data.memberships : [],
    };
  } catch (err) {
    console.warn("[squadsApi] adminPlayerByRef failed:", err?.message || err);
    return { ok: false, code: "server_error" };
  }
}

/**
 * Roll every open membership of `fromSeason` forward into `toSeason`
 * (idempotent — a player already squadded in `toSeason` is skipped). Returns
 * { ok:true, created, skipped } or { ok:false, code } (invalid_seasons,
 * forbidden, server_error).
 */
export async function adminRollSquads(fromSeason, toSeason) {
  try {
    const { data, error } = await supabase.rpc("admin_roll_squads", {
      p_from_season: fromSeason,
      p_to_season: toSeason,
    });
    if (error) {
      console.warn("[squadsApi] adminRollSquads failed:", error.message);
      return rpcErrorResult(error);
    }
    return { ok: true, created: data?.created ?? 0, skipped: data?.skipped ?? 0 };
  } catch (err) {
    console.warn("[squadsApi] adminRollSquads failed:", err?.message || err);
    return { ok: false, code: "server_error" };
  }
}

/**
 * Invoke the squad-build Edge Function — rebuilds a season's squads from
 * Rugby League Project. `dryRun:true` (the default) computes and returns the
 * summary without writing; `dryRun:false` with `prune:true` applies it and
 * removes seed memberships for players no longer in the plan (admin-made
 * memberships are never pruned). Returns { ok:true, season, matchesRead,
 * requestsLive, skippedTeams, playersMatched, playersCreated, refsStamped,
 * membershipsWritten, pruned, changes[] } or { ok:false, code }.
 */
export async function runSquadBuild({ season, dryRun = true, prune = false } = {}) {
  let response;
  try {
    response = await supabase.functions.invoke("squad-build", {
      body: { ...(season != null ? { season } : {}), dry_run: dryRun, prune },
    });
  } catch {
    return { ok: false, code: "network_error" };
  }
  if (response.error) return functionErrorResult(response.error, SQUAD_BUILD_CODES);
  const d = response.data ?? {};
  return {
    ok: true,
    season: d.season,
    matchesRead: d.matches_read,
    requestsLive: d.requests_live,
    skippedTeams: d.skipped_teams,
    playersMatched: d.players_matched,
    playersCreated: d.players_created,
    refsStamped: d.refs_stamped,
    membershipsWritten: d.memberships_written,
    pruned: d.pruned,
    changes: Array.isArray(d.changes) ? d.changes : [],
  };
}

/**
 * Resolve every NRL team's id from the fixtures table (the only
 * authenticated-readable source of team ids — `teams` itself is
 * service_role-only). Reads home/away team id + ref pairs across every
 * fixture and returns a Map keyed by the team's ref (the same full team name
 * used by TEAM_NAMES/NRL_TEAMS), so a team picker can resolve a chosen name
 * to the uuid the squad RPCs require.
 */
export async function fetchTeamIdsByName() {
  try {
    const { data, error } = await supabase
      .from("fixtures")
      .select("home_team_id, home_ref, away_team_id, away_ref");
    if (error) {
      console.warn("[squadsApi] could not load team ids:", error.message);
      return null;
    }
    const byName = new Map();
    for (const row of data ?? []) {
      if (row.home_team_id && row.home_ref) byName.set(row.home_ref, row.home_team_id);
      if (row.away_team_id && row.away_ref) byName.set(row.away_ref, row.away_team_id);
    }
    return byName;
  } catch (err) {
    console.warn("[squadsApi] could not load team ids:", err?.message || err);
    return null;
  }
}
